import React, { useState, useEffect } from 'react';
import { db } from '../../firebase';
import { ref, onValue, update } from 'firebase/database';
import { useNavigate } from 'react-router-dom';
import InchargeLayout from './InchargeLayout';
import {
    FaPrint,
    FaFileExcel,
    FaCopy,
    FaSearch,
    FaEye,
    FaStamp,
    FaFileAlt,
    FaBook,
    FaRegFileAlt,
    FaTimes
} from 'react-icons/fa';
import { toast } from 'react-toastify';

const ShowSubeChallan = () => {
    const navigate = useNavigate();
    const [registrations, setRegistrations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    // Modal states
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [updateStatus, setUpdateStatus] = useState('');
    const [isUpdating, setIsUpdating] = useState(false);

    // Print Modal states
    const [isPrintModalOpen, setIsPrintModalOpen] = useState(false);
    const [printData, setPrintData] = useState(null);

    useEffect(() => {
        const regRef = ref(db, 'ekamwatapzp/EngineerRegistrations');
        const unsubscribe = onValue(regRef, (snapshot) => {
            if (snapshot.exists()) {
                const data = snapshot.val();
                const list = Object.keys(data).map(key => ({
                    id: key,
                    ...data[key]
                }));
                setRegistrations(list);
            } else {
                setRegistrations([]);
            }
            setLoading(false);
        });

        return () => unsubscribe();
    }, []);

    const filteredData = registrations.filter(item =>
        `${item.firstName} ${item.surname}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.whatsappNo?.includes(searchTerm) ||
        item.transactionId?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const getStatusColor = (status) => {
        switch (status) {
            case 'Approved': return 'text-emerald-600';
            case 'Rejected': return 'text-rose-600';
            case 'Pending': return 'text-amber-500';
            default: return 'text-gray-600';
        }
    };

    const openModal = (user) => {
        setSelectedUser(user);
        setUpdateStatus(user.assistantStatus || '');
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setSelectedUser(null);
        setUpdateStatus('');
    };

    const handlePrintOpen = (item) => {
        setPrintData(item);
        setIsPrintModalOpen(true);
    };

    const handleUpdateStatus = async () => {
        if (!updateStatus) {
            toast.warning("Status निवडा");
            return;
        }
        setIsUpdating(true);
        try {
            const userRef = ref(db, `ekamwatapzp/EngineerRegistrations/${selectedUser.id}`);
            await update(userRef, {
                assistantStatus: updateStatus,
                statusUpdatedAt: new Date().toISOString()
            });
            toast.success("स्थिती यशस्वीरित्या अपडेट केली!");
            closeModal();
        } catch (error) {
            console.error("Update Error:", error);
            toast.error("अपडेट करताना त्रुटी आली.");
        } finally {
            setIsUpdating(false);
        }
    };

    return (
        <InchargeLayout>
            <div className="min-h-screen bg-gray-50 flex flex-col  relative">
                {/* Header Section */}
                <div className="px-6 py-6 flex flex-col md:flex-row md:items-center justify-between gap-4 print:hidden">
                    <h1 className="text-xl font-medium text-gray-700">नवीन सु.बे. अभियंत्यांची यादी</h1>
                    <div className="flex items-center text-sm font-medium text-gray-500 gap-2">
                        <span>सांगली जिल्हा परिषद</span>
                        <span>/</span>
                        <span className="text-gray-600 font-semibold">नवीन सु.बे. अभियंत्यांची यादी</span>
                    </div>
                </div>

                <div className="px-6 pb-12 print:hidden">
                    <div className="max-w-[100%] mx-auto bg-white rounded-lg shadow-sm border border-gray-200 p-6">

                        {/* Status & Legend Header */}
                        <div className="flex flex-col gap-4 mb-6 text-right">
                            <div className="flex items-center justify-end gap-6 text-xs font-bold text-gray-600">
                                <div className="flex items-center gap-2">
                                    <span className="w-3 h-3 rounded-full bg-amber-500"></span>
                                    <span>निलंबन</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="w-3 h-3 rounded-full bg-emerald-600"></span>
                                    <span>मंजूर</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="w-3 h-3 rounded-full bg-rose-600"></span>
                                    <span>नाकारले</span>
                                </div>
                            </div>

                            <div className="flex flex-wrap items-center justify-end gap-x-6 gap-y-2 text-[11px] font-bold text-gray-500">
                                <div className="flex items-center gap-1.5 grayscale opacity-70">
                                    <FaStamp className="text-rose-400" /> <span>प्रमाणपत्रधारक</span>
                                </div>
                                <div className="flex items-center gap-1.5">
                                    <FaFileAlt className="text-emerald-500" /> <span>अर्जदाराची कागदपत्रे</span>
                                </div>
                                <div className="flex items-center gap-1.5 grayscale opacity-70">
                                    <FaPrint className="text-gray-400" /> <span>प्रिंट</span>
                                </div>
                                <div className="flex items-center gap-1.5 grayscale opacity-70">
                                    <FaBook className="text-gray-400" /> <span>चलन अपलोड केले</span>
                                </div>
                            </div>
                        </div>

                        {/* Search & Buttons */}
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
                            <div className="flex items-center rounded overflow-hidden shadow-sm border border-gray-300 h-9">
                                <button className="bg-[#6c757d] hover:bg-[#5a6268] text-white px-4 text-xs font-semibold border-r border-gray-400 h-full">Copy</button>
                                <button className="bg-[#6c757d] hover:bg-[#5a6268] text-white px-4 text-xs font-semibold border-r border-gray-400 h-full">Excel</button>
                                <button className="bg-[#6c757d] hover:bg-[#5a6268] text-white px-4 text-xs font-semibold h-full">Print</button>
                            </div>

                            <div className="flex items-center gap-2">
                                <label className="text-sm font-medium text-gray-600">Search:</label>
                                <input
                                    type="text"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="border border-gray-300 rounded px-3 py-1.5 text-sm outline-none w-64 italic"
                                    placeholder="नावाने शोधा..."
                                />
                            </div>
                        </div>

                        {/* Table */}
                        <div className="overflow-x-auto border border-gray-200 rounded">
                            <table className="w-full text-left text-sm border-collapse italic">
                                <thead>
                                    <tr className="bg-gray-50 text-gray-700 font-bold border-b border-gray-200 uppercase text-[11px] tracking-wider">
                                        <th className="px-4 py-4 border-r border-gray-200 w-16 text-center">क्र. ↑↓</th>
                                        <th className="px-4 py-4 border-r border-gray-200 min-w-[200px]">अर्जदाराचे नाव ↑↓</th>
                                        <th className="px-4 py-4 border-r border-gray-200">फोन क्र. ↑↓</th>
                                        <th className="px-4 py-4 border-r border-gray-200">ट्रा-झॅक्शन आयडी ↑↓</th>
                                        <th className="px-4 py-4 border-r border-gray-200">रक्कम ↑↓</th>
                                        <th className="px-4 py-4 border-r border-gray-200">अकाउंटंट फी चलन स्थिती ↑↓</th>
                                        <th className="px-4 py-4 text-center">चलन अपडेट करा / पहा ↑↓</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    {loading ? (
                                        <tr><td colSpan="7" className="text-center py-10 text-gray-400 font-bold">लोड होत आहे...</td></tr>
                                    ) : filteredData.length > 0 ? (
                                        filteredData.map((item, index) => (
                                            <tr key={item.id} className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50/20'} hover:bg-blue-50/40 transition-all group`}>
                                                <td className="px-4 py-5 border-r border-gray-200 text-center font-bold text-gray-500">{index + 1}</td>
                                                <td className="px-4 py-5 border-r border-gray-200 font-bold text-gray-800 uppercase">
                                                    {item.firstName} {item.surname}
                                                </td>
                                                <td className="px-4 py-5 border-r border-gray-200 font-bold text-gray-600">{item.whatsappNo}</td>
                                                <td className="px-4 py-5 border-r border-gray-200 text-[11px] font-bold text-blue-600">{(item.transactionId || 'TXN12345678').toUpperCase()}</td>
                                                <td className="px-4 py-5 border-r border-gray-200 font-bold text-gray-700">₹ {item.amount || '10,000'}</td>
                                                <td className={`px-4 py-5 border-r border-gray-200 font-bold uppercase text-[11px] ${getStatusColor(item.assistantStatus)}`}>
                                                    {item.assistantStatus || 'Pending'}
                                                </td>
                                                <td className="px-4 py-5">
                                                    <div className="flex items-center justify-center gap-5">
                                                        <button
                                                            onClick={() => openModal(item)}
                                                            className="text-emerald-500 hover:scale-125 transition-transform"
                                                            title="अर्जदाराची कागदपत्रे"
                                                        >
                                                            <FaFileAlt size={16} />
                                                        </button>
                                                        <button
                                                            onClick={() => handlePrintOpen(item)}
                                                            className="text-gray-400 hover:text-blue-500 transition-colors"
                                                            title="प्रिंट"
                                                        >
                                                            <FaPrint size={15} />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr><td colSpan="7" className="text-center py-16 text-gray-400 font-bold tracking-widest">कोणताही डेटा उपलब्ध नाही.</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                {/* MODAL WINDOW */}
                {isModalOpen && (
                    <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4">
                        {/* Backdrop */}
                        <div
                            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
                            onClick={closeModal}
                        ></div>

                        {/* Modal Content */}
                        <div className="relative bg-white w-full max-w-md rounded shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-200">
                            {/* Modal Header */}
                            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 ">
                                <h2 className="text-xl font-bold text-gray-800">Update Fee Challan Status</h2>
                                <button
                                    onClick={closeModal}
                                    className="text-gray-400 hover:text-gray-600 transition-colors"
                                >
                                    <FaTimes size={20} />
                                </button>
                            </div>

                            {/* Modal Body */}
                            <div className="p-8 space-y-6">
                                <div className="text-xl font-bold text-blue-500 uppercase">
                                    {selectedUser?.firstName} {selectedUser?.surname}
                                </div>

                                <div className="space-y-3">
                                    <label className="text-[15px] font-bold text-gray-700 italic flex items-center gap-1">
                                        Status <span className="text-red-500 font-bold">*</span>
                                    </label>
                                    <div className="relative">
                                        <select
                                            value={updateStatus}
                                            onChange={(e) => setUpdateStatus(e.target.value)}
                                            className="w-full bg-white border border-gray-300 rounded-md px-4 py-2.5 text-sm text-gray-700 outline-none appearance-none cursor-pointer focus:ring-2 focus:ring-blue-500/20 italic font-medium"
                                        >
                                            <option value="">Select Status</option>
                                            <option value="Approved">Approved</option>
                                            <option value="Rejected">Rejected</option>
                                        </select>
                                        <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Modal Footer */}
                            <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end items-center gap-3">
                                <button
                                    onClick={closeModal}
                                    className="px-6 py-2 bg-[#6c757d] text-white rounded text-sm font-bold shadow-md hover:bg-gray-600 transition transform active:scale-95 italic"
                                >
                                    Close
                                </button>
                                <button
                                    onClick={handleUpdateStatus}
                                    disabled={isUpdating}
                                    className="px-6 py-2 bg-[#5c6dec] text-white rounded text-sm font-bold shadow-md hover:bg-blue-700 transition transform active:scale-95 italic min-w-[100px]"
                                >
                                    {isUpdating ? 'Updating...' : 'Update'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* PRINT MODAL WINDOW */}
                {isPrintModalOpen && printData && (
                    <div className="fixed inset-0 z-[2000] flex items-center justify-center p-4 bg-gray-900/60 backdrop-blur-sm print-overlay">
                        <div className="bg-white w-full max-w-[1000px] h-[90vh] overflow-y-auto rounded-lg shadow-2xl print:shadow-none print:h-auto print:w-full print:max-w-none print:m-0 print:overflow-hidden relative">
                            {/* Header - Hidden on print */}
                            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gray-50 print:hidden sticky top-0 z-10">
                                <div className="flex items-center gap-4">
                                    <h2 className="text-2xl font-semibold text-gray-700 ">चलन डाउनलोड</h2>
                                </div>
                                <div className="flex items-center gap-4">
                                    <span className="text-lg text-gray-400">सांगली जिल्हा परिषद / चलन डाउनलोड करा</span>
                                    <button
                                        onClick={() => setIsPrintModalOpen(false)}
                                        className="text-gray-400 hover:text-gray-600 transition-colors bg-white shadow-sm p-1 rounded"
                                    >
                                        <FaTimes size={20} />
                                    </button>
                                </div>
                            </div>

                            {/* Modal Body / Printable Area */}
                            <div className="p-8 print:p-0 bg-gray-50/30">
                                <div className="flex justify-end mb-6 print:hidden">
                                    <button
                                        onClick={() => window.print()}
                                        className="bg-[#5c6dec] hover:bg-blue-600 text-white px-5 py-2.5 rounded text-sm font-bold flex items-center gap-2 shadow-md hover:shadow-lg transition-all"
                                    >
                                        <FaPrint /> Download Payment Receipt
                                    </button>
                                </div>

                                {/* Actual Print Canvas */}
                                <div id="printable-area" className="border border-black max-w-[950px] mx-auto text-sm text-black relative bg-white" style={{ fontFamily: 'Arial, sans-serif' }}>

                                    {/* Top Section */}
                                    <div className="flex border-b border-black">
                                        <div className="w-[80%] p-4 border-r border-black font-bold text-xs leading-relaxed">
                                            <p className="text-sm font-bold mb-2">Payment Info</p>
                                            <div className="grid grid-cols-[140px_auto] gap-y-1">
                                                <span>Transaction Id</span><span> : {printData.transactionId || 'TX202508211732046781'}</span>
                                                <span>Payment Status</span><span> : Paid</span>
                                                <span>Amount</span><span> : {printData.amount || '10000.0'}</span>
                                                <span>Payment Date</span><span> : 21 Aug 2025 05:33:44</span>
                                                <span>Account No.</span><span> : 60374858603 (Bank Of Maharashtra)</span>
                                            </div>
                                        </div>
                                        <div className="w-[20%] p-4"></div>
                                    </div>

                                    {/* Challan No Row */}
                                    <div className="flex border-b border-black text-xs font-bold divide-x divide-black h-10 items-center">
                                        <div className="px-3 w-[15%]">चलन प्रथम प्रत</div>
                                        <div className="px-3 w-[25%] flex justify-center">पैसे भरण्याकरीता</div>
                                        <div className="px-3 w-[40%] flex justify-center">Challan No. : ZP2026276</div>
                                        <div className="px-3 w-[20%]">21-08-2025</div>
                                    </div>

                                    {/* Bank Info Row */}
                                    <div className="p-4 border-b border-black text-xs font-bold leading-tight">
                                        <p>सांगली जिल्हा मध्यवर्ती सहकारी बँक लि.</p>
                                        <p>मध्ये भरलेल्या रोख रकमेचे चलन</p>
                                        <p className="mt-5 text-[11px] font-normal">for creadit to the Zilla Parishad सांगली A/C -  60374858603 (Bank Of Maharashtra)  with the Head Office</p>
                                    </div>

                                    {/* Main Grid */}
                                    <div className="flex">
                                        {/* Left Side (80%) */}
                                        <div className="w-[80%] border-r border-black flex flex-col text-xs">
                                            <div className="p-2 border-b border-black text-center font-bold">
                                                रक्कम पाठविणाऱ्याने भरावयाचे
                                            </div>
                                            <div className="flex border-b border-black">
                                                <div className="w-[60%] p-3 border-r border-black font-medium">रक्कम कोणी आणून दिली त्याचे नाव-</div>
                                                <div className="w-[40%] p-3 font-bold">{printData.firstName} {printData.surname}</div>
                                            </div>
                                            <div className="flex border-b border-black min-h-[70px]">
                                                <div className="w-[60%] p-3 border-r border-black font-medium">ज्याच्या वतीने भरण्यात आली त्याचे नाव व पत्ता-</div>
                                                <div className="w-[40%] p-3 font-bold leading-relaxed">कार्यकारी अभियंता<br />बांधकाम विभाग<br />(पश्चिम) जि.प.सांगली</div>
                                            </div>
                                            <div className="p-3 border-b border-black font-medium">
                                                पाठविलेल्या रकमेचा व अधिकारपत्र (कोणतेही असल्यास) त्याचा पूर्ण तपशील<br />
                                                <span className="font-bold">(नोंदणी फी रक्कम)</span>
                                            </div>

                                            {/* Tapshil Headers */}
                                            <div className="flex border-b border-black h-8 text-center items-center font-medium">
                                                <div className="w-[60%] pl-3 border-r border-black text-left">तपशील</div>
                                                <div className="w-[40%] pl-3 border-black text-left">रक्कम</div>
                                            </div>
                                            <div className="flex border-b border-black h-8 font-medium">
                                                <div className="w-[60%] p-0 border-r border-black text-left"></div>
                                                <div className="w-[30%] pl-3 pt-1 border-r border-black text-left">रुपये</div>
                                                <div className="w-[10%] pl-3 pt-1 border-gray-400 text-left">पैसे</div>
                                            </div>

                                            {/* Main Details Grid inside Left */}
                                            <div className="flex border-b border-black">
                                                {/* Left part of detail */}
                                                <div className="w-[60%] border-r border-black flex flex-col font-medium">
                                                    <div className="p-3 border-b border-black h-[50px]">
                                                        धनादेश क्र.<br />दिनांक
                                                    </div>
                                                    <div className="flex border-b border-black">
                                                        <div className="w-1/2 p-3 border-r border-black min-h-[50px]">लेखाशिर्ष</div>
                                                        <div className="w-1/2 p-3 min-h-[50px]">विभाग-</div>
                                                    </div>
                                                    <div className="p-3 border-b border-black h-[50px]">
                                                        प्रमाणक क्र.<br />दिनांक
                                                    </div>
                                                    <div className="p-3 font-bold h-10">
                                                        अक्षरी रुपये - रुपये {printData.amount === 10000 || printData.amount === '10000' ? 'दहा हजार' : printData.amount} फक्त
                                                    </div>
                                                </div>
                                                {/* Right part of detail */}
                                                <div className="w-[30%] border-r border-black flex items-center justify-center font-bold text-sm">
                                                    {printData.amount || '10000.0'}/-
                                                </div>
                                                <div className="w-[10%] flex items-center justify-center font-bold">
                                                </div>
                                            </div>

                                            {/* Sign Area */}
                                            <div className="p-5 border-b border-black text-center text-xs h-20 flex items-end justify-center">
                                                <div>कार्यकारी अभियंता (बांधकाम)<br />जिल्हा परिषद सांगली</div>
                                            </div>

                                            {/* Dates Area */}
                                            <div className="flex border-b border-black h-10 items-center">
                                                <div className="w-1/2 px-3 border-r border-black h-full flex items-center">वरीलप्रमाणे रक्कम मिळाली.</div>
                                                <div className="w-1/2 px-3 h-full flex items-center">तारीख</div>
                                            </div>
                                            <div className="flex h-10 items-center">
                                                <div className="w-1/2 px-3 border-r border-black h-full flex items-center">तारीख</div>
                                                <div className="w-1/2 px-3 h-full flex items-center">शाखा-सांगली</div>
                                            </div>
                                        </div>

                                        {/* Right Side (20%) */}
                                        <div className="w-[20%] flex flex-col text-xs font-bold leading-relaxed relative">
                                            <div className="p-4 pt-[4.5rem]">
                                                खात्याचे नाव<br />
                                                <span className="font-normal block mt-2 ml-2">
                                                    1. जिल्हा परिषद सांगली<br />
                                                    2. पंचायत समिती<br />
                                                    3. जिल्हा फंड
                                                </span>
                                            </div>
                                            <div className="absolute top-[48%] left-0 right-0 p-4 font-bold text-xs leading-loose">
                                                12 बांधकाम (3)<br />
                                                (क) इतर बाबी (नोंदणी फी)<br />
                                                0059-0107
                                            </div>
                                            {/* Bottom absolute mapping matches the left side bottom layout exact row height */}
                                            <div className="absolute top-[496px] left-0 right-0 flex border-t border-b border-black h-10">
                                                <div className="w-[75%] border-r border-black flex items-center px-4">{printData.amount || '10000.0'}/-</div>
                                                <div className="w-[25%] flex items-center justify-center">0</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <style dangerouslySetInnerHTML={{
                            __html: `
                            @media print {
                                body, html {
                                    background-color: white !important;
                                    margin: 0 !important;
                                    padding: 0 !important;
                                    height: 100% !important;
                                }
                                .print-overlay {
                                    position: fixed !important;
                                    top: 0 !important;
                                    left: 0 !important;
                                    right: 0 !important;
                                    bottom: 0 !important;
                                    background: white !important;
                                    z-index: 99999 !important;
                                    display: flex !important;
                                    justify-content: center !important;
                                    align-items: flex-start !important;
                                    padding-top: 20px !important;
                                    overflow: visible !important;
                                }
                                #printable-area { 
                                    border: 1px solid black !important;
                                }
                            }
                        `}} />
                    </div>
                )}
            </div>
        </InchargeLayout>
    );
};

export default ShowSubeChallan;
